import { COLOR_ACCENT, COLOR_MUTED, COLOR_TEXT, FONT_INTER } from '../../blueprint-view/shared/blueprint-view-styles';

export const TOOLBAR_SURFACE = '#ffffff';
export const TOOLBAR_SURFACE_SUBTLE = '#f2f6f9';
export const TOOLBAR_BORDER = '#d5dce3';
export const TOOLBAR_BORDER_STRONG = '#a9b4bf';
export const TOOLBAR_TEXT = COLOR_TEXT;
export const TOOLBAR_TEXT_MUTED = COLOR_MUTED;
export const TOOLBAR_ACCENT = COLOR_ACCENT;
export const TOOLBAR_ACCENT_SOFT = '#e6f6fd';
export const TOOLBAR_DANGER = '#d64545';
export const TOOLBAR_DANGER_SOFT = '#fdeeee';

export const TOOLBAR_RADIUS = '8px';
export const TOOLBAR_RADIUS_SM = '5px';
export const TOOLBAR_SHADOW = '0 8px 24px rgba(16, 32, 52, 0.16), 0 1px 3px rgba(16, 32, 52, 0.10)';
export const TOOLBAR_CONTROL_SIZE = 26;
export const TOOLBAR_ICON_SIZE = 15;
export const TOOLBAR_FONT = FONT_INTER;

/** px reserved at each edge so the centred toolbar can never cover the presets panel (left, ends at 260) or the nav/export bar (right) */
export const TOOLBAR_EDGE_GUTTER = 272;

export const toolbarShellStyle: React.CSSProperties = {
	position: 'absolute',
	top: 8,
	left: TOOLBAR_EDGE_GUTTER,
	right: TOOLBAR_EDGE_GUTTER,
	zIndex: 1000,
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	pointerEvents: 'none',
	fontFamily: TOOLBAR_FONT
};

export const toolbarSurfaceStyle: React.CSSProperties = {
	maxWidth: '100%',
	background: TOOLBAR_SURFACE,
	border: `1px solid ${TOOLBAR_BORDER}`,
	borderRadius: TOOLBAR_RADIUS,
	boxShadow: TOOLBAR_SHADOW,
	pointerEvents: 'auto',
	overflow: 'visible',
	color: TOOLBAR_TEXT
};

export const toolbarRowStyle: React.CSSProperties = {
	display: 'flex',
	alignItems: 'stretch',
	gap: '0px',
	padding: '3px 4px 4px',
	flexWrap: 'wrap',
	justifyContent: 'center',
	rowGap: '4px'
};

export const toolbarPropertyRowStyle: React.CSSProperties = {
	...toolbarRowStyle,
	borderTop: `1px solid ${TOOLBAR_BORDER}`,
	background: TOOLBAR_SURFACE_SUBTLE,
	borderRadius: `0 0 ${TOOLBAR_RADIUS} ${TOOLBAR_RADIUS}`
};

/** A whole group is ONE flex item: its caption and controls can never be split onto different lines by a wrap, and its divider travels with it */
export const toolbarGroupStyle = (withSeparator: boolean): React.CSSProperties => ({
	display: 'inline-flex',
	flexDirection: 'column',
	alignItems: 'stretch',
	flex: '0 0 auto',
	gap: '1px',
	padding: '1px 8px 0',
	borderLeft: withSeparator ? `1px solid ${TOOLBAR_BORDER}` : undefined,
	whiteSpace: 'nowrap'
});

export const toolbarGroupLabelStyle: React.CSSProperties = {
	fontSize: '9px',
	fontWeight: 700,
	letterSpacing: '0.07em',
	textTransform: 'uppercase',
	color: TOOLBAR_TEXT_MUTED,
	userSelect: 'none',
	textAlign: 'center',
	lineHeight: 1.3
};

export const toolbarGroupControlsStyle: React.CSSProperties = {
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: '2px'
};

/** The caption for a single control inside a group -- what makes a bare number ("0.4") say what it measures */
export const toolbarControlLabelStyle: React.CSSProperties = {
	fontSize: '10px',
	fontWeight: 600,
	color: TOOLBAR_TEXT_MUTED,
	userSelect: 'none',
	padding: '0 1px 0 3px'
};

export const toolbarReadoutStyle: React.CSSProperties = {
	minWidth: '30px',
	textAlign: 'center',
	fontSize: '11px',
	fontWeight: 600,
	fontVariantNumeric: 'tabular-nums',
	color: TOOLBAR_TEXT,
	userSelect: 'none'
};

export type ToolbarControlStyleParams = {
	isActive?: boolean;
	isHovered?: boolean;
	disabled?: boolean;
	tone?: 'default' | 'danger';
	hasLabel?: boolean;
};

export const toolbarControlStyle = (params: ToolbarControlStyleParams): React.CSSProperties => {
	const { isActive = false, isHovered = false, disabled = false, tone = 'default', hasLabel = false } = params;
	const isDanger = tone === 'danger';
	const accent = isDanger ? TOOLBAR_DANGER : TOOLBAR_ACCENT;
	const accentSoft = isDanger ? TOOLBAR_DANGER_SOFT : TOOLBAR_ACCENT_SOFT;

	return {
		height: `${TOOLBAR_CONTROL_SIZE}px`,
		minWidth: `${TOOLBAR_CONTROL_SIZE}px`,
		padding: hasLabel ? '0 7px' : '0 4px',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: '4px',
		boxSizing: 'border-box',
		border: `1px solid ${isActive ? accent : isHovered && !disabled ? TOOLBAR_BORDER_STRONG : 'transparent'}`,
		borderRadius: TOOLBAR_RADIUS_SM,
		background: isActive ? accentSoft : isHovered && !disabled ? TOOLBAR_SURFACE_SUBTLE : 'transparent',
		color: disabled ? TOOLBAR_TEXT_MUTED : isDanger ? TOOLBAR_DANGER : TOOLBAR_TEXT,
		cursor: disabled ? 'not-allowed' : 'pointer',
		opacity: disabled ? 0.4 : 1,
		fontFamily: TOOLBAR_FONT,
		fontSize: '11px',
		fontWeight: isActive ? 600 : 500,
		lineHeight: 1,
		transition: 'background 90ms ease, border-color 90ms ease'
	};
};

export const toolbarPanelStyle: React.CSSProperties = {
	position: 'absolute',
	top: `${TOOLBAR_CONTROL_SIZE + 6}px`,
	zIndex: 1001,
	background: TOOLBAR_SURFACE,
	border: `1px solid ${TOOLBAR_BORDER}`,
	borderRadius: TOOLBAR_RADIUS,
	boxShadow: TOOLBAR_SHADOW,
	padding: '8px',
	fontFamily: TOOLBAR_FONT,
	fontSize: '11px',
	color: TOOLBAR_TEXT
};

export const toolbarPanelHeadingStyle: React.CSSProperties = {
	fontSize: '9px',
	fontWeight: 700,
	letterSpacing: '0.06em',
	textTransform: 'uppercase',
	color: TOOLBAR_TEXT_MUTED,
	userSelect: 'none',
	margin: '0 0 5px 1px'
};

/** A transparent-checker background so a "no fill" swatch reads as empty rather than as white */
export const TOOLBAR_CHECKER_BACKGROUND = 'linear-gradient(45deg, #d0d7de 25%, transparent 25%, transparent 75%, #d0d7de 75%), linear-gradient(45deg, #d0d7de 25%, transparent 25%, transparent 75%, #d0d7de 75%)';

/** Paints a colour, or the transparent checker when there is none -- the one place "no colour" gets its look */
export const toolbarPaintBackgroundStyle = (color: string | null, checkerSize = 8): React.CSSProperties => ({
	background: color ?? TOOLBAR_CHECKER_BACKGROUND,
	backgroundColor: color ?? TOOLBAR_SURFACE,
	backgroundSize: color ? undefined : `${checkerSize}px ${checkerSize}px`,
	backgroundPosition: color ? undefined : `0 0, ${checkerSize / 2}px ${checkerSize / 2}px`
});

export type ToolbarSwatchStyleParams = {
	color: string | null;
	isSelected?: boolean;
	size?: number;
};

export const toolbarSwatchStyle = (params: ToolbarSwatchStyleParams): React.CSSProperties => {
	const { color, isSelected = false, size = 18 } = params;

	return {
		width: `${size}px`,
		height: `${size}px`,
		padding: 0,
		boxSizing: 'border-box',
		borderRadius: '4px',
		border: `${isSelected ? 2 : 1}px solid ${isSelected ? TOOLBAR_ACCENT : TOOLBAR_BORDER_STRONG}`,
		...toolbarPaintBackgroundStyle(color),
		cursor: 'pointer',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center'
	};
};

/** The toolbar's own show/hide tab -- one affordance in two states, so both ends round toward the surface they sit against */
export const toolbarTabStyle = (isCollapsed: boolean): React.CSSProperties => ({
	pointerEvents: 'auto',
	display: 'flex',
	alignItems: 'center',
	gap: '5px',
	padding: '4px 12px',
	border: `1px solid ${TOOLBAR_BORDER}`,
	borderTop: isCollapsed ? `1px solid ${TOOLBAR_BORDER}` : 'none',
	borderRadius: isCollapsed ? TOOLBAR_RADIUS : `0 0 ${TOOLBAR_RADIUS} ${TOOLBAR_RADIUS}`,
	background: TOOLBAR_SURFACE,
	boxShadow: isCollapsed ? TOOLBAR_SHADOW : 'none',
	cursor: 'pointer',
	fontFamily: TOOLBAR_FONT,
	fontSize: '11px',
	fontWeight: 600,
	letterSpacing: '0.04em',
	textTransform: 'uppercase',
	color: TOOLBAR_TEXT_MUTED
});

export const toolbarInputStyle: React.CSSProperties = {
	height: `${TOOLBAR_CONTROL_SIZE}px`,
	boxSizing: 'border-box',
	padding: '0 6px',
	border: `1px solid ${TOOLBAR_BORDER}`,
	borderRadius: TOOLBAR_RADIUS_SM,
	background: TOOLBAR_SURFACE,
	fontFamily: TOOLBAR_FONT,
	fontSize: '11px',
	color: TOOLBAR_TEXT
};

/** A selectable row inside a popover panel: the font list, and anything else that offers one choice per line */
export const toolbarPanelRowStyle = (isSelected: boolean): React.CSSProperties => ({
	display: 'block',
	width: '100%',
	padding: '4px 6px',
	border: 'none',
	borderRadius: '4px',
	background: isSelected ? TOOLBAR_SURFACE_SUBTLE : 'transparent',
	cursor: 'pointer',
	fontSize: '12px',
	fontWeight: isSelected ? 600 : 400,
	color: 'inherit',
	textAlign: 'left'
});
